import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import EmployeesPage from "./pages/EmployeesPage";
import EmployeeDetailPage from "./pages/EmployeeDetailPage";
import AttendancePage from "./pages/AttendancePage";
import LeavesPage from "./pages/LeavesPage";
import PayrollPage from "./pages/PayrollPage";
import PayrollDashboardPage from "./pages/PayrollDashboardPage";
import MyPayslipsPage from "./pages/MyPayslipsPage";
import SkillsPage from "./pages/SkillsPage";
import ContributionsPage from "./pages/ContributionsPage";
import AIAssistantPage from "./pages/AIAssistantPage";
import ATSPage from "./pages/ATSPage";
import OKRsPage from "./pages/OKRsPage";
import AssetsPage from "./pages/AssetsPage";
import ExpensesPage from "./pages/ExpensesPage";
import BenefitsPage from "./pages/BenefitsPage";
import ResearchPage from "./pages/ResearchPage";
import LearningPage from "./pages/LearningPage";
import ReportsPage from "./pages/ReportsPage";
import SettingsPage from "./pages/SettingsPage";
import AdminIntegrationsPage from "./pages/AdminIntegrationsPage";
import AdminSecurityPage from "./pages/AdminSecurityPage";
import OnboardingPage from "./pages/OnboardingPage";
import HolidaysPage from "./pages/HolidaysPage";
import LettersPage from "./pages/LettersPage";
import ApprovalsPage from "./pages/ApprovalsPage";
import RBACPage from "./pages/RBACPage";
import TravelPage from "./pages/TravelPage";
import ProfilePage from "./pages/ProfilePage";
import TeamKernelPage from "./pages/TeamKernelPage";
import BillingPage from "./pages/BillingPage";
import WishesPage from "./pages/WishesPage";
import GopalPage from "./pages/GopalPage";
import CompliancePage from "./pages/CompliancePage";
import PoliciesPage from "./pages/PoliciesPage";
import OnboardingCompliancePage from "./pages/OnboardingCompliancePage";
import EngagePage from "./pages/EngagePage";
import ProgramsPage from "./pages/ProgramsPage";
import HelpdeskPage from "./pages/HelpdeskPage";
import LoansPage from "./pages/LoansPage";
import WittiesPage from "./pages/WittiesPage";
import PerformancePage from "./pages/PerformancePage";
import WordlePage from "./pages/WordlePage";
import ThoughtLibraryPage from "./pages/ThoughtLibraryPage";
import InsightsPage from "./pages/InsightsPage";
import InternshipsPage from "./pages/InternshipsPage";
import ExitManagementPage from "./pages/ExitManagementPage";

// Layout
import MainLayout from "./components/layout/MainLayout";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = null }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="employees" element={<EmployeesPage />} />
        <Route path="employees/:id" element={<EmployeeDetailPage />} />
        <Route path="attendance" element={<AttendancePage />} />
        <Route path="leaves" element={<LeavesPage />} />
        <Route path="holidays" element={<HolidaysPage />} />
        <Route path="payroll" element={<PayrollPage />} />
        <Route path="payroll-dashboard" element={<PayrollDashboardPage />} />
        <Route path="my-payslips" element={<MyPayslipsPage />} />
        <Route path="skills" element={<SkillsPage />} />
        <Route path="contributions" element={<ContributionsPage />} />
        <Route path="ai-assistant" element={<AIAssistantPage />} />
        <Route path="onboarding" element={<OnboardingPage />} />
        <Route path="ats" element={<ATSPage />} />
        <Route path="okrs" element={<OKRsPage />} />
        <Route path="assets" element={<AssetsPage />} />
        <Route path="expenses" element={<ExpensesPage />} />
        <Route path="benefits" element={<BenefitsPage />} />
        <Route path="research" element={<ResearchPage />} />
        <Route path="learning" element={<LearningPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="letters" element={<LettersPage />} />
        <Route path="travel" element={<TravelPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="rbac" element={<RBACPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="admin/integrations" element={<AdminIntegrationsPage />} />
        <Route path="admin/security" element={<AdminSecurityPage />} />
        <Route path="team-kernel" element={<TeamKernelPage />} />
        <Route path="approvals" element={<ApprovalsPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="wishes" element={<WishesPage />} />
        <Route path="gopal" element={<GopalPage />} />
        <Route path="compliance" element={<CompliancePage />} />
        <Route path="policies" element={<PoliciesPage />} />
        <Route path="onboarding-compliance" element={<OnboardingCompliancePage />} />
        <Route path="my-onboarding" element={<OnboardingCompliancePage />} />
        <Route path="engage" element={<EngagePage />} />
        <Route path="programs" element={<ProgramsPage />} />
        <Route path="helpdesk" element={<HelpdeskPage />} />
        <Route path="loans" element={<LoansPage />} />
        <Route path="witties" element={<WittiesPage />} />
        <Route path="performance" element={<PerformancePage />} />
        <Route path="wordle" element={<WordlePage />} />
        <Route path="thought-library" element={<ThoughtLibraryPage />} />
        <Route path="insights" element={<InsightsPage />} />
        <Route path="internships" element={<InternshipsPage />} />
        <Route path="exit-management" element={<ExitManagementPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
